import Modal from '@components/Modal';
import { useModal } from '@components/Modal/ModalProvider';
import { useCallback, useRef } from 'react';
import BaseModalContent from '../BaseReportModalContent/BaseReportModalContetnt';
import AutoCaptureModalContent from './AutoCaptureModalContent';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import useAuth from '@hooks/Auth/useAuth';
import { DicomNodeParams } from '@models/pacs';
import {
  ChamberInfoItem,
  iImageItem,
  PACSInfo,
  send_info_to_pacs,
  SendInfoItem,
  ServerCaptureSendInfoItem,
} from 'utils/PacsUtilities';
import { BufferData } from 'utils/captureUtils';
import useModalActions from '@hooks/common/useModalAction';
import { useConfig } from 'config/useConfig';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import useGetDicomNodeConfig from '@hooks/DicomNode/useDicomNode';
import { loadConfig } from '@api/api';
import { getConfiguredCprCrossSectionImageMode } from '@features/analysis/rendering/Panels/cprImageModeConfig';

export interface Send_PACS_Data {
  buffers: BufferData[];
  screen_capture: boolean;
  capture_kind: string;
  series_description: string;
  windowWidth?: string;
  windowCenter?: string;
  thickness?: number;
  cropsize?: number[];
}

export type AutoCaptureMode =
  | 'CPRSingleCapture'
  | 'CPRMainCapture'
  | 'CPRFullCapture'
  | 'LesionStackCapture'
  | 'Surface3DCapture'
  | 'VRCapture'
  | '4chamberCapture'
  | '2chamberCapture'
  | 'shortaxisCapture'
  | 'axialCapture'
  | 'fullChamberCapture'
  | 'reportCapture'
  | 'fullscreenCapture';

interface AutoCaptureModeMessageConfig {
  prefix: string;
}

const autoCaptureModeMessageConfig: Record<
  AutoCaptureMode,
  AutoCaptureModeMessageConfig
> = {
  CPRSingleCapture: { prefix: 'CPR Single Capture' },
  CPRMainCapture: { prefix: 'CPR Main Capture' },
  CPRFullCapture: { prefix: 'CPR Full Capture' },
  LesionStackCapture: { prefix: 'Lesion Stack Capture' },
  Surface3DCapture: { prefix: '3D View Capture' },
  VRCapture: { prefix: '3D View Capture' },
  '4chamberCapture': { prefix: '4-Chamber Capture' },
  '2chamberCapture': { prefix: '2-Chamber Capture' },
  shortaxisCapture: { prefix: 'Shortaxis Capture' },
  axialCapture: { prefix: 'Axial Capture' },
  fullChamberCapture: { prefix: 'Full Chamber Capture' },
  reportCapture: { prefix: 'Report Capture' },
  fullscreenCapture: { prefix: 'Fullscreen Capture' },
};

function createAutoCaptureMessage(type: AutoCaptureMode, message: string) {
  const { prefix } = autoCaptureModeMessageConfig[type];

  if (!prefix) return message;
  if (!message) return prefix;

  return `${prefix}: ${message}`;
}

interface AutoCaptureOpenOptions {
  onCompleted?: (result: any) => void;
  serverSendInfo?: ServerCaptureSendInfoItem[];
}

let autoCaptureSendQueue = Promise.resolve();

function enqueueAutoCaptureSend(task: () => Promise<void>) {
  autoCaptureSendQueue = autoCaptureSendQueue.then(task).catch((error) => {
    console.error('[AutoCaptureModal] queued send failed', error);
  });
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Capture upload failed';
}

function createCaptureResultMessage(result: any) {
  if (result?.status === 200 || result?.status === 201) {
    const cond = result.data?.data?.condition;
    let message = cond?.message?.header ?? '';
    const errorMessage = cond?.message?.error;

    if (Array.isArray(errorMessage)) {
      for (const messageItem of errorMessage) {
        message += messageItem;
      }
    } else if (typeof errorMessage === 'string') {
      message += errorMessage;
    }

    return {
      success: Boolean(cond?.status),
      message,
    };
  }

  return {
    success: false,
    message: result?.message ?? 'Capture upload failed',
  };
}

function waitForNextRenderFrame() {
  if (typeof requestAnimationFrame !== 'function') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

async function scheduleCaptureMessage(
  type: AutoCaptureMode,
  success: boolean,
  message: string,
) {
  scheduleRender('MainShell', {
    type: 'captureMessage',
    success,
    message: createAutoCaptureMessage(type, message),
  });

  await waitForNextRenderFrame();
}

export function useAutoCaptureModal() {
  const ref = useRef<string | null>(null);
  const { openModal, closeModal } = useModal();
  const { selectedSeriesId, selectedSeriesImageTag } = useBrowserPage();
  const { data: dicomNodeConfig } = useGetDicomNodeConfig();
  const { config } = useConfig();

  const { accessToken } = useAuth();

  function create_pacs_info(node: DicomNodeParams): PACSInfo {
    return {
      AETITLE: dicomNodeConfig?.dicomAetitle!,
      HOST_URL: node.ip,
      PORT: String(node.port),
      REMOTE_AETITLE: node.aetitle,
      SEND_MODE: config.sendmode,
    };
  }

  async function sendpaceChember(
    selectedRow: DicomNodeParams[],
    sendinfo: ChamberInfoItem[],
  ) {
    const BASE_URL = await loadConfig();
    let pacs_info: PACSInfo[] = [];
    for (const row of selectedRow) {
      const pacsinfo = create_pacs_info(row);
      pacs_info.push(pacsinfo);
    }

    const json = {
      image_filter: 2,
      Base_seriesNumber: dicomNodeConfig?.seriesNumberBase!,
      SERVER_URL: BASE_URL,
      PACS_INFO: pacs_info,
      SendInfo: sendinfo,
    };
    const pacs_result = await send_info_to_pacs(accessToken, {
      series_id: selectedSeriesId!,
      json,
      image_data: [],
    });

    return pacs_result;
  }

  async function sendpacsServerCapture(
    selectedRow: DicomNodeParams[],
    sendinfo: ServerCaptureSendInfoItem[],
  ) {
    const BASE_URL = await loadConfig();
    let pacs_info: PACSInfo[] = [];
    for (const row of selectedRow) {
      const pacsinfo = create_pacs_info(row);
      pacs_info.push(pacsinfo);
    }

    const normalizedSendInfo = sendinfo.map((item) => ({
      ...item,
      windowWidth:
        item.windowWidth || String(selectedSeriesImageTag?.windowWidth ?? ''),
      windowCenter:
        item.windowCenter || String(selectedSeriesImageTag?.windowCenter ?? ''),
      thickness:
        item.thickness > 0
          ? item.thickness
          : Math.max(1, Number(selectedSeriesImageTag?.sliceThickness ?? 1)),
      planar_spacing:
        item.planar_spacing > 0
          ? item.planar_spacing
          : Number(selectedSeriesImageTag?.pixelSpacing ?? 1),
      vertical_spacing:
        item.vertical_spacing > 0
          ? item.vertical_spacing
          : Number(selectedSeriesImageTag?.planarSpacingInterpolated ?? 1),
      newWidth: item.newWidth || config.captureWidth || config.uploadWidth,
      newHeight: item.newHeight || config.captureHeight || config.uploadHeight,
      labels: null,
    }));

    const json = {
      image_filter: 2,
      Base_seriesNumber: dicomNodeConfig?.seriesNumberBase!,
      SERVER_URL: BASE_URL,
      PACS_INFO: pacs_info,
      SendInfo: normalizedSendInfo,
    };

    const pacs_result = await send_info_to_pacs(accessToken, {
      series_id: selectedSeriesId!,
      json,
      image_data: [],
    });

    return pacs_result;
  }

  async function sendpacs(
    selectedRow: DicomNodeParams[],
    datas: Send_PACS_Data[] | undefined,
    opts?: {
      newWidth?: number;
      newHeight?: number;
    },
  ) {
    const BASE_URL = await loadConfig();
    let pacs_info: PACSInfo[] = [];
    for (const row of selectedRow) {
      const pacsinfo = create_pacs_info(row);
      pacs_info.push(pacsinfo);
    }

    let send_info: SendInfoItem[] = [];
    let image_data: iImageItem[] = [];

    if (!datas || datas.length === 0) return false;

    let upW = config.uploadWidth;
    let upH = config.uploadHeight;
    if (opts) {
      upW = opts.newWidth !== undefined ? opts.newWidth : upW;
      upH = opts.newHeight !== undefined ? opts.newHeight : upH;
    }

    const timename = Date.now();
    for (let c = 0; c < datas.length; c++) {
      const data = datas[c];
      const {
        buffers,
        screen_capture,
        capture_kind,
        series_description,
        cropsize,
      } = data;
      let thick = Number(selectedSeriesImageTag?.sliceThickness);
      if (data.thickness !== undefined) {
        thick = data.thickness < 1 ? 1 : data.thickness;
      }
      const sendinfo: SendInfoItem = {
        windowWidth:
          data.windowWidth !== undefined
            ? data.windowWidth
            : String(selectedSeriesImageTag?.windowWidth),
        windowCenter:
          data.windowCenter !== undefined
            ? data.windowCenter
            : String(selectedSeriesImageTag?.windowCenter),
        SliceCount: buffers.length,
        thickness: thick,
        planar_spacing: selectedSeriesImageTag?.pixelSpacing!,
        vertical_spacing: selectedSeriesImageTag?.planarSpacingInterpolated!,
        screen_capture: screen_capture,
        capture_kind: capture_kind,
        series_description: series_description,
        newWidth: upW,
        newHeight: upH,
        labels: [],
        draw_description: capture_kind === 'CPR',
      };
      let drawDescription:boolean = false;
      for (let i = 0; i < buffers.length; i++) {
        const pathname = `${timename}_${c}_${i}.${buffers[i].extension}`;
        drawDescription = buffers[i].branch_name ? true : false;
        sendinfo.labels.push({
          path: pathname,
          width: buffers[i].width,
          height: buffers[i].height,
          orientation: buffers[i].orientation ?? [],
          position: buffers[i].position ?? [],
          crop_size: cropsize === undefined ? -1 : cropsize[i],
          cpr_info: buffers[i].cprinfo,
          text: buffers[i].branch_name ?? '',
        });
        image_data.push({
          image: buffers[i].buffer,
          name: pathname,
        });
      }
      sendinfo.draw_description ||= drawDescription;
      send_info.push(sendinfo);
    }

    const json = {
      image_filter: 2,
      Base_seriesNumber: dicomNodeConfig?.seriesNumberBase!,
      SERVER_URL: BASE_URL,
      PACS_INFO: pacs_info,
      SendInfo: send_info,
    };
    const pacs_result = await send_info_to_pacs(accessToken, {
      series_id: selectedSeriesId!,
      json,
      image_data,
    });

    return pacs_result;
  }

  async function sendAutoCaptureAndNotify(
    info: DicomNodeParams[],
    type: AutoCaptureMode,
    data_temp: Send_PACS_Data[] | undefined,
    opts?: AutoCaptureOpenOptions,
  ) {
    let result: any;
    let completedResult: any;

    try {
      if (type === 'LesionStackCapture') {
        result = await sendpacsServerCapture(info, opts?.serverSendInfo ?? []);
      } else if (
        type === 'CPRSingleCapture' ||
        type === 'CPRMainCapture' ||
        type === 'CPRFullCapture'
      ) {
        if (getConfiguredCprCrossSectionImageMode() === 'server') {
          result = await sendpacsServerCapture(info, opts?.serverSendInfo ?? []);
        } else {
          result = await sendpacs(info, data_temp);
        }
      } else if (type === 'Surface3DCapture' || type === 'VRCapture') {
        if (opts?.serverSendInfo?.length) {
          result = await sendpacsServerCapture(info, opts.serverSendInfo);
        } else {
          result = await sendpacs(info, data_temp);
        }
      } else if (
        type === '2chamberCapture' ||
        type === '4chamberCapture' ||
        type === 'shortaxisCapture' ||
        type === 'axialCapture' ||
        type === 'fullChamberCapture'
      ) {
        const arr_str: ChamberInfoItem[] = [];
        const chamberInfoMap = {
          '2chamberCapture': {
            capture_kind: 'Chamber2',
            series_description: '2-chamber',
          },
          '4chamberCapture': {
            capture_kind: 'Chamber4',
            series_description: '4-chamber',
          },
          shortaxisCapture: {
            capture_kind: 'ShortAxis',
            series_description: 'Short axis',
          },
          axialCapture: {
            capture_kind: 'Axial',
            series_description: 'Axial',
          },
        } as const;

        if (type === 'fullChamberCapture') {
          config.chamberCaptureOrder.forEach((captureType) => {
            const chamberInfo =
              chamberInfoMap[captureType as keyof typeof chamberInfoMap];
            if (chamberInfo) arr_str.push({ ...chamberInfo });
          });
        } else {
          const chamberInfo =
            chamberInfoMap[type as keyof typeof chamberInfoMap];
          if (chamberInfo) arr_str.push({ ...chamberInfo });
        }
        result = await sendpaceChember(info, arr_str);
      } else if (type === 'reportCapture' || type === 'fullscreenCapture') {
        result = await sendpacs(info, data_temp, {
          newWidth: -1,
          newHeight: -1,
        });
      }

      console.log(result);

      const { success, message } = createCaptureResultMessage(result);
      await scheduleCaptureMessage(type, success, message);
      completedResult = result;
    } catch (error) {
      console.error('[AutoCaptureModal] send failed', error);
      await scheduleCaptureMessage(type, false, getErrorMessage(error));
      completedResult = error;
    }

    try {
      opts?.onCompleted?.(completedResult);
    } catch (error) {
      console.warn('[AutoCaptureModal] onCompleted failed', error);
    }
  }

  function handleClickFunction(
    info: DicomNodeParams[],
    type: AutoCaptureMode,
    data_temp: Send_PACS_Data[] | undefined,
    opts?: AutoCaptureOpenOptions,
  ) {
    handleCloseModal();
    enqueueAutoCaptureSend(() =>
      sendAutoCaptureAndNotify(info, type, data_temp, opts),
    );
  }

  const handleCloseModal = useCallback(() => {
    if (ref.current) {
      closeModal(ref.current);
      ref.current = null;
    }
  }, [closeModal]);

  const AutoCaptureModalOpen = (
    type: AutoCaptureMode,
    data: Send_PACS_Data[] | undefined,
    opts?: AutoCaptureOpenOptions,
  ) => {
    if (config.defaultServer.length > 0) {
      handleClickFunction(config.defaultServer, type, data, opts);
      return;
    }

    ref.current = openModal(
      <Modal
        type="page"
        close={handleCloseModal}
        description={
          <BaseModalContent
            onClose={handleCloseModal}
            title={'Select the server'}
            vw={40}
            vh={40}
            capturebutton={false}
          >
            <AutoCaptureModalContent
              onConfirm={(info) => handleClickFunction(info, type, data, opts)}
            />
          </BaseModalContent>
        }
      />,
      false,
      handleCloseModal,
    );
  };

  return AutoCaptureModalOpen;
}
